import React, { useEffect, useReducer, useState } from 'react';
import { TextField, MenuItem, Button, Box } from '@mui/material';
import FSearchDropdown, { Item } from './SearchDropdown';
import { FBasicPropos } from '../../../interfaces/User.interface';
import { Controller, Control, FieldErrors, Field, useFormContext } from "react-hook-form";
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete'
import * as Yup from 'yup';
import { ValidationError } from 'yup';

interface RowData {
  name: string;
  quantity: number;
  index: number;
}

interface ActionRowData extends RowData {
  type: 'add' | 'remove' | 'update';
}

const rowDataSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  quantity: Yup.number().required('Quantity is required').min(1, 'Quantity must be at least 1'),
});

const RowComponent: React.FC<{
  index: number,
  searchData: Item[],
  onChange: (value: ActionRowData) => void;
  initialRows?: RowData;
}> = ({ index, searchData, onChange, initialRows }) => {

  const [data, setData] = useState<RowData>({ name: '', quantity: 1, index });
  
  if (initialRows) {
    setData(initialRows);
  }

  const handleSearchDropdownChange = (name: string) => {
    setData((prevData) => ({ ...prevData, name }));
    onChange({ ...data, type: 'update', name });

  };

  const handleQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // console.log("Change value", Number(event.target.value))
    setData((prevData) => ({ ...prevData, quantity: Number(event.target.value) }));
    onChange({ ...data, type: 'update', quantity: Number(event.target.value) });
  };

  const onRemove = () => {
    onChange({ ...data, type: 'remove' });
  };

  return (
    <Box className="flex space-x-2">
      <FSearchDropdown
        className="flex-auto w-10/12"
        searchData={searchData}
        searchFields={['name']}
        onChange={handleSearchDropdownChange}
 
      />

      <TextField
        className='flex-auto w-1/12'
        label="Quantity"
        type="number"
        value={data.quantity || ''}
        onChange={handleQuantityChange}
      
      />

      <IconButton
        className="flex-auto w-1/12"
        onClick={onRemove}
        color="error"
        aria-label="delete"
      >
        <DeleteIcon />
      </IconButton>
    </Box>
  );
};



let count = 0;

const FMultiRowComponent: React.FC<FBasicPropos> = ({ field, control, errors }) => {
  const searchData = field.data;
  const { setValue } = useFormContext();
  const purchaseProductReducer = (selectedProducts: RowData[], action: ActionRowData): RowData[] => {
    switch (action.type) {
      case 'add':
        return [...selectedProducts, { name: action.name, quantity: action.quantity, index: action.index }];
      case 'remove':
        return selectedProducts.filter((product, _) => product.index !== action.index);
      case 'update': {
        const updatedProducts = selectedProducts.map((product, _) => {
          if (product.index === action.index) {
            return { name: action.name, quantity: action.quantity, index: action.index };
          } else {
            return product;
          }
        });
        return updatedProducts;
      }
      default:
        return selectedProducts;
    }
  };

  const initialRows: RowData[] = [{ name: '', quantity: 1, index: 0 }];

  const [selectedProducts, dispatch] = useReducer(purchaseProductReducer, initialRows);

  useEffect(() => {
    setValue(field.name, selectedProducts, { shouldValidate: false });
  }, [selectedProducts]);
  const handleAddRow = () => {
    dispatch({ type: 'add', name: '', quantity: 1, index: ++count });
  };

  const name = field.name;
  return (
    <Controller
      name={name}
      control={control}
      defaultValue=""
      render={({ field: controllerField }) => {
        const dispatchWrap = (value: ActionRowData) => {
          dispatch(value);
        }
        return (
          <div className='space-y-2 p-4'>
            {selectedProducts.map((product) => (
              <RowComponent
                key={product.index} // Adding a unique key for each row
                index={product.index}
                searchData={searchData}
                onChange={dispatchWrap}
              />
            ))}
            <Button
              onClick={handleAddRow}
              variant="outlined" // Use 'outlined' for better border visibility
              color="primary"
              className="w-full"
            >
              Add Product
            </Button>
          </div>
        );
      }}
    />
  );
};

export default FMultiRowComponent;
